export interface StreamingChunkOptions {
  /** Chunking mode */
  mode: 'character' | 'word' | 'fixed-length' | 'block' | 'sentence'
  /** Delay between chunks in milliseconds */
  delay?: number
  /** For fixed-length mode: characters per chunk */
  chunkSize?: number
  /** For word mode: words per chunk */
  wordsPerChunk?: number
  /** For block mode: custom block separators */
  blockSeparators?: string[]
}

export interface StreamChunk {
  /** The chunked content */
  content: string
  /** Index of this chunk */
  index: number
  /** Total number of chunks */
  total: number
  /** Whether this is the final chunk */
  isLast: boolean
  /** Progress percentage (0-100) */
  progress: number
}

/**
 * Creates word-based chunks while preserving spaces
 */
function createWordBasedChunks(text: string, wordsPerChunk: number = 1): string[] {
  if (!text.trim()) return ['']

  const words = text.split(/(\s+)/) // Split but keep separators
  const chunks: string[] = []
  let currentChunk = ''
  let wordCount = 0

  for (const part of words) {
    currentChunk += part

    // Count actual words (non-whitespace)
    if (part.trim()) {
      wordCount++

      if (wordCount >= wordsPerChunk) {
        chunks.push(currentChunk)
        currentChunk = ''
        wordCount = 0
      }
    }
  }

  // Add remaining content
  if (currentChunk) {
    chunks.push(currentChunk)
  }

  return chunks.length > 0 ? chunks : ['']
}

/**
 * Creates sentence-based chunks
 */
function createSentenceBasedChunks(text: string): string[] {
  if (!text.trim()) return ['']

  // Split by sentence endings but keep the punctuation
  const sentences = text.split(/([.!?]+\s*)/).filter(Boolean)
  const chunks: string[] = []
  let currentChunk = ''

  for (let i = 0; i < sentences.length; i += 2) {
    const sentence = sentences[i] || ''
    const punctuation = sentences[i + 1] || ''
    currentChunk += sentence + punctuation

    if (punctuation.match(/[.!?]/)) {
      chunks.push(currentChunk)
      currentChunk = ''
    }
  }

  // Add any remaining content
  if (currentChunk.trim()) {
    chunks.push(currentChunk)
  }

  return chunks.length > 0 ? chunks : [text]
}

/**
 * Creates block-based chunks using custom separators
 */
function createBlockBasedChunks(text: string, separators: string[] = ['\n\n', '\n']): string[] {
  if (!text.trim()) return ['']

  let chunks = [text]

  // Apply each separator in order
  for (const separator of separators) {
    const newChunks: string[] = []

    for (const chunk of chunks) {
      const parts = chunk.split(separator)
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) {
          // Add the separator back except for the last part
          newChunks.push(separator)
        }
        if (parts[i]) {
          newChunks.push(parts[i])
        }
      }
    }

    chunks = newChunks.filter(chunk => chunk.length > 0)
  }

  return chunks.length > 0 ? chunks : [text]
}

/**
 * Creates chunks based on the specified mode
 */
function createChunks(text: string, options: StreamingChunkOptions): string[] {
  switch (options.mode) {
    case 'character':
      // For character mode, we want cumulative chunks (char by char buildup)
      return text.split('').map((_, i) => text.slice(0, i + 1))
    
    case 'word':
      // For word mode, return individual word chunks (not cumulative)
      return createWordBasedChunks(text, options.wordsPerChunk || 1)
    
    case 'fixed-length':
      {
        // For fixed-length, return individual chunks (not cumulative)
        const chunkSize = options.chunkSize || 10
        const chunks: string[] = []
        for (let i = 0; i < text.length; i += chunkSize) {
          chunks.push(text.slice(i, i + chunkSize))
        }
        return chunks
      }
    
    case 'sentence':
      // For sentence mode, return individual sentences (not cumulative)
      return createSentenceBasedChunks(text)
    
    case 'block':
      // For block mode, return individual blocks (not cumulative)
      return createBlockBasedChunks(text, options.blockSeparators)
    
    default:
      return [text]
  }
}

/**
 * Creates a ReadableStream that chunks text according to the specified options
 */
export function createTextStream(text: string, options: StreamingChunkOptions): ReadableStream<StreamChunk> {
  const chunks = createChunks(text, options)
  const delay = options.delay || 100
  let currentIndex = 0
  
  return new ReadableStream<StreamChunk>({
    async start(controller) {
      try {
        for (let i = 0; i < chunks.length; i++) {
          // Check if stream was cancelled
          if (controller.desiredSize === null) {
            break
          }
          
          const chunk: StreamChunk = {
            content: chunks[i], // Send individual chunks, not accumulated
            index: i,
            total: chunks.length,
            isLast: i === chunks.length - 1,
            progress: Math.round(((i + 1) / chunks.length) * 100)
          }
          
          controller.enqueue(chunk)
          currentIndex = i + 1
          
          // Add delay between chunks (except for the last one)
          if (!chunk.isLast && delay > 0) {
            await new Promise(resolve => setTimeout(resolve, delay))
          }
        }
        
        controller.close()
      } catch (error) {
        controller.error(error)
      }
    },
    
    cancel() {
      console.log(`Stream cancelled at chunk ${currentIndex}`)
    }
  })
}

/**
 * Creates a ReadableStream compatible with Vercel AI SDK format
 */
export function createVercelAIStream(text: string, options: StreamingChunkOptions): ReadableStream<Uint8Array> {
  const textStream = createTextStream(text, options)
  const encoder = new TextEncoder()

  return new ReadableStream<Uint8Array>({
    async start(controller) {
      const reader = textStream.getReader()

      try {
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            // Send final completion message
            const finalMessage = 'd:{"finishReason":"stop","usage":{"promptTokens":0,"completionTokens":0}}\n'
            controller.enqueue(encoder.encode(finalMessage))
            break
          }

          // Format as Vercel AI SDK data stream format
          const dataMessage = `0:${JSON.stringify(value.content)}\n`
          controller.enqueue(encoder.encode(dataMessage))
        }

        controller.close()
      } catch (error) {
        controller.error(error)
      } finally {
        reader.releaseLock()
      }
    }
  })
}

/**
 * Utility to create structured JSON streaming
 */
export interface StructuredContentBlock {
  type: string
  attrs?: Record<string, any>
  text: string
}

export function createStructuredStream(
  blocks: StructuredContentBlock[],
  options: Omit<StreamingChunkOptions, 'mode'> & { textMode?: 'character' | 'word' | 'sentence' }
): ReadableStream<StreamChunk> {
  const textMode = options.textMode || 'word'
  const delay = options.delay || 150

  return new ReadableStream<StreamChunk>({
    async start(controller) {
      const currentContent: any[] = []
      let totalChunks = 0
      let currentChunk = 0

      // Calculate total chunks
      for (const block of blocks) {
        const textChunks = createChunks(block.text, { mode: textMode, ...options })
        totalChunks += textChunks.length
      }

      try {
        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
          const block = blocks[blockIndex]
          const textChunks = createChunks(block.text, { mode: textMode, ...options })

          // Add new block with empty text
          const newBlock = {
            type: block.type,
            ...(block.attrs && { attrs: block.attrs }),
            content: [{ type: 'text', text: '' }]
          }
          currentContent.push(newBlock)

                   // Stream text into this block
          let accumulatedText = ''
          for (let textIndex = 0; textIndex < textChunks.length; textIndex++) {
            if (controller.desiredSize === null) break
            
            // Accumulate text chunks (since they're now individual chunks, not cumulative)
            if (textMode === 'character') {
              accumulatedText = textChunks[textIndex] // Character mode is already cumulative
            } else {
              accumulatedText += textChunks[textIndex] // Other modes need accumulation
            }
            
            // Update the text content
            currentContent[blockIndex].content[0].text = accumulatedText

            const jsonContent = JSON.stringify(currentContent)
            const chunk: StreamChunk = {
              content: jsonContent,
              index: currentChunk,
              total: totalChunks,
              isLast: currentChunk === totalChunks - 1,
              progress: Math.round(((currentChunk + 1) / totalChunks) * 100)
            }

            controller.enqueue(chunk)
            currentChunk++

            if (!chunk.isLast && delay > 0) {
              await new Promise(resolve => setTimeout(resolve, delay))
            }
          }
        }

        controller.close()
      } catch (error) {
        controller.error(error)
      }
    }
  })
}

/**
 * Helper function to create a mock API response stream
 */
export function createMockAPIResponse(text: string, options: StreamingChunkOptions): Response {
  const stream = createVercelAIStream(text, options)

  return new Response(stream, {
    headers: {
      'Content-Type': 'text/plain; charset=utf-8',
    },
  })
} 